import React, { useState, useContext, useEffect, useMemo } from "react";
import { IoMdCheckmark } from "react-icons/io";
import Equipments from "./Equipments";
import DefaultFee from "./DefaultFee";
import { Stack } from "@mui/material";
import PercentageInput from "./input-elements/PercentageInput";
import CurrencyInput from "./input-elements/CurrencyInput";
import { EquipmentContext } from "../useContext/context";
import { Form } from "react-bootstrap";
import ToggleSwitch from "./input-elements/ToggleSwitch";
import * as Yup from "yup";
import pricingValidation from "../pricing-validation";
import ToggleSwitchContainer from "./input-elements/ToggleSwitchContainer";
import { EMS_OTHER_DEFAULT_FEE } from "../../../../constants";
import ACHPricing from "./ACHPricing";
const tiered_validation = pricingValidation.pricingValidation.tiered;

const TieredPricing = ({
    emsEquipments,
    setFormDataByKeyValue,
    errors,
    form_data,
    allEquipments,
    removeError: removeErrorEq,
    isEditable = true,
}) => {
    const { setErrors, pricingDefaults } = useContext(EquipmentContext);
    const [tieredDefaults, setTieredDefaults] = useState({});

    // Update tiered defaults when pricing defaults load
    useEffect(() => {
        if (pricingDefaults && pricingDefaults.tiered) {
            setTieredDefaults(pricingDefaults.tiered);
        }
    }, [pricingDefaults]);

    // Set default values when component mounts or tiered defaults change
    useEffect(() => {
        const defaultValues = {
            tiered_qual_rate: tieredDefaults.tiered_qual_rate?.default || tiered_validation.tiered_qual_rate.default,
            tiered_qual_transaction: tieredDefaults.tiered_qual_transaction?.default || tiered_validation.tiered_qual_transaction.default,
            tiered_qual_debit_rate: tieredDefaults.tiered_qual_debit_rate?.default || tiered_validation.tiered_qual_debit_rate.default,
            tiered_qual_debit_per_transaction: tieredDefaults.tiered_qual_debit_per_transaction?.default || tiered_validation.tiered_qual_debit_per_transaction.default,
            tiered_mid_qual_rate: tieredDefaults.tiered_mid_qual_rate?.default || tiered_validation.tiered_mid_qual_rate.default,
            tiered_mid_qual_transaction: tieredDefaults.tiered_mid_qual_transaction?.default || tiered_validation.tiered_mid_qual_transaction.default,
            tiered_mid_qual_debit_rate: tieredDefaults.tiered_mid_qual_debit_rate?.default || tiered_validation.tiered_mid_qual_debit_rate.default,
            tiered_mid_qual_debit_per_transaction: tieredDefaults.tiered_mid_qual_debit_per_transaction?.default || tiered_validation.tiered_mid_qual_debit_per_transaction.default,
            tiered_non_qual_rate: tieredDefaults.tiered_non_qual_rate?.default || tiered_validation.tiered_non_qual_rate.default,
            tiered_non_qual_transaction: tieredDefaults.tiered_non_qual_transaction?.default || tiered_validation.tiered_non_qual_transaction.default,
            tiered_non_qual_debit_rate: tieredDefaults.tiered_non_qual_debit_rate?.default || tiered_validation.tiered_non_qual_debit_rate.default,
            tiered_non_qual_debit_per_transaction: tieredDefaults.tiered_non_qual_debit_per_transaction?.default || tiered_validation.tiered_non_qual_debit_per_transaction.default,
            amex_tier_one_rate: tieredDefaults.amex_tier_one_rate?.default || tiered_validation.amex_tier_one_rate.default,
            amex_tier_one_fee: tieredDefaults.amex_tier_one_fee?.default || tiered_validation.amex_tier_one_fee.default,
            amex_tier_two_rate: tieredDefaults.amex_tier_two_rate?.default || tiered_validation.amex_tier_two_rate.default,
            amex_tier_two_fee: tieredDefaults.amex_tier_two_fee?.default || tiered_validation.amex_tier_two_fee.default,
            amex_tier_three_rate: tieredDefaults.amex_tier_three_rate?.default || tiered_validation.amex_tier_three_rate.default,
            amex_tier_three_fee: tieredDefaults.amex_tier_three_fee?.default || tiered_validation.amex_tier_three_fee.default,
            amex_tier_four_rate: tieredDefaults.amex_tier_four_rate?.default || tiered_validation.amex_tier_four_rate.default,
            amex_tier_four_fee: tieredDefaults.amex_tier_four_fee?.default || tiered_validation.amex_tier_four_fee.default,
            tiered_amex_flat_rate: tieredDefaults.tiered_amex_flat_rate?.default || tiered_validation.tiered_amex_flat_rate.default,
            tiered_amex_flat_transaction: tieredDefaults.tiered_amex_flat_transaction?.default || tiered_validation.tiered_amex_flat_transaction.default,
            tiered_amex_ic_plus_rate: tieredDefaults.tiered_amex_ic_plus_rate?.default || tiered_validation.tiered_amex_ic_plus_rate.default,
            tiered_amex_ic_plus_transaction: tieredDefaults.tiered_amex_ic_plus_transaction?.default || tiered_validation.tiered_amex_ic_plus_transaction.default,
            tiered_pin_debit_rate: tieredDefaults.tiered_pin_debit_rate?.default || tiered_validation.tiered_pin_debit_rate.default,
            tiered_pin_debit_per_transaction: tieredDefaults.tiered_pin_debit_per_transaction?.default || tiered_validation.tiered_pin_debit_per_transaction.default,
            tiered_pin_debit_network_fee: "no",
            add_ach: "no",
            ebt: "no",
            fnf_number: "",
            ebt_transactions: "",
            tiered_amex_structure: "flat_rate",
        };
        Object.entries(defaultValues).forEach(([key, value]) => {
            if (form_data[key] === undefined || form_data[key] === "") {
                setFormDataByKeyValue(key, value);
            }
        });
    }, [tieredDefaults]);
    // Memoized validation schema using current defaults
    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            // Qualified Fields
            tiered_qual_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Qualified Rate is required")
                .min(
                    tieredDefaults.tiered_qual_rate?.minimum || tiered_validation.tiered_qual_rate.minimum,
                    `Please enter a rate of ${(tieredDefaults.tiered_qual_rate?.minimum || tiered_validation.tiered_qual_rate.minimum).toFixed(2)}% or higher.`,
                ),
            tiered_qual_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Qualified Transaction Fee is required")
                .min(
                    tieredDefaults.tiered_qual_transaction?.minimum || tiered_validation.tiered_qual_transaction.minimum,
                    `Please enter a fee of $${(tieredDefaults.tiered_qual_transaction?.minimum || tiered_validation.tiered_qual_transaction.minimum).toFixed(2)} or higher.`,
                ),
            tiered_qual_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Qualified Debit Rate is required")
                .min(
                    tieredDefaults.tiered_qual_debit_rate?.minimum || tiered_validation.tiered_qual_debit_rate.minimum,
                    `Please enter a rate of ${(tieredDefaults.tiered_qual_debit_rate?.minimum || tiered_validation.tiered_qual_debit_rate.minimum).toFixed(2)}% or higher.`,
                ),
            tiered_qual_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Qualified Debit Per Transaction fee is required")
                .min(
                    tieredDefaults.tiered_qual_debit_per_transaction?.minimum || tiered_validation.tiered_qual_debit_per_transaction.minimum,
                    `Please enter a fee of $${(tieredDefaults.tiered_qual_debit_per_transaction?.minimum || tiered_validation.tiered_qual_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            // Mid-Qualified Fields
            tiered_mid_qual_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Mid-Qualified Rate is required")
                .min(
                    tieredDefaults.tiered_mid_qual_rate?.minimum || tiered_validation.tiered_mid_qual_rate.minimum,
                    `Please enter a rate of ${(tieredDefaults.tiered_mid_qual_rate?.minimum || tiered_validation.tiered_mid_qual_rate.minimum).toFixed(2)}% or higher.`,
                ),
            tiered_mid_qual_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Mid-Qualified Transaction Fee is required")
                .min(
                    tieredDefaults.tiered_mid_qual_transaction?.minimum || tiered_validation.tiered_mid_qual_transaction.minimum,
                    `Please enter a fee of $${(tieredDefaults.tiered_mid_qual_transaction?.minimum || tiered_validation.tiered_mid_qual_transaction.minimum).toFixed(2)} or higher.`,
                ),
            tiered_mid_qual_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Mid-Qualified Debit Rate is required")
                .min(
                    tieredDefaults.tiered_mid_qual_debit_rate?.minimum || tiered_validation.tiered_mid_qual_debit_rate.minimum,
                    `Please enter a rate of ${(tieredDefaults.tiered_mid_qual_debit_rate?.minimum || tiered_validation.tiered_mid_qual_debit_rate.minimum).toFixed(2)}% or higher.`,
                ),
            tiered_mid_qual_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Mid-Qualified Debit Per Transaction fee is required")
                .min(
                    tieredDefaults.tiered_mid_qual_debit_per_transaction?.minimum || tiered_validation.tiered_mid_qual_debit_per_transaction.minimum,
                    `Please enter a fee of $${(tieredDefaults.tiered_mid_qual_debit_per_transaction?.minimum || tiered_validation.tiered_mid_qual_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            // Non-Qualified Fields
            tiered_non_qual_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Non-Qualified Rate is required")
                .min(
                    tieredDefaults.tiered_non_qual_rate?.minimum || tiered_validation.tiered_non_qual_rate.minimum,
                    `Please enter a rate of ${(tieredDefaults.tiered_non_qual_rate?.minimum || tiered_validation.tiered_non_qual_rate.minimum).toFixed(2)}% or higher.`,
                ),
            tiered_non_qual_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Non-Qualified Transaction Fee is required")
                .min(
                    tieredDefaults.tiered_non_qual_transaction?.minimum || tiered_validation.tiered_non_qual_transaction.minimum,
                    `Please enter a fee of $${(tieredDefaults.tiered_non_qual_transaction?.minimum || tiered_validation.tiered_non_qual_transaction.minimum).toFixed(2)} or higher.`,
                ),
            tiered_non_qual_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Non-Qualified Debit Rate is required")
                .min(
                    tieredDefaults.tiered_non_qual_debit_rate?.minimum || tiered_validation.tiered_non_qual_debit_rate.minimum,
                    `Please enter a rate of ${(tieredDefaults.tiered_non_qual_debit_rate?.minimum || tiered_validation.tiered_non_qual_debit_rate.minimum).toFixed(2)}% or higher.`,
                ),
            tiered_non_qual_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Non-Qualified Debit Per Transaction fee is required")
                .min(
                    tieredDefaults.tiered_non_qual_debit_per_transaction?.minimum || tiered_validation.tiered_non_qual_debit_per_transaction.minimum,
                    `Please enter a fee of $${(tieredDefaults.tiered_non_qual_debit_per_transaction?.minimum || tiered_validation.tiered_non_qual_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            // Tiered Rate fields
            // AMEX Tier 1
            amex_tier_one_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "tiered",
                    then: Yup.number()
                        .required("AMEX Tier 1 Rate is required")
                        .min(
                            tieredDefaults.amex_tier_one_rate?.minimum || tiered_validation.amex_tier_one_rate.minimum,
                            `Please enter a rate of ${(tieredDefaults.amex_tier_one_rate?.minimum || tiered_validation.amex_tier_one_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),
            amex_tier_one_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "tiered",
                    then: Yup.number()
                        .required("AMEX Tier 1 Fee is required")
                        .min(
                            tieredDefaults.amex_tier_one_fee?.minimum || tiered_validation.amex_tier_one_fee.minimum,
                            `Please enter a fee of $${(tieredDefaults.amex_tier_one_fee?.minimum || tiered_validation.amex_tier_one_fee.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            // AMEX Tier 2
            amex_tier_two_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "tiered",
                    then: Yup.number()
                        .required("AMEX Tier 2 Rate is required")
                        .min(
                            tieredDefaults.amex_tier_two_rate?.minimum || tiered_validation.amex_tier_two_rate.minimum,
                            `Please enter a rate of ${(tieredDefaults.amex_tier_two_rate?.minimum || tiered_validation.amex_tier_two_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),
            amex_tier_two_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "tiered",
                    then: Yup.number()
                        .required("AMEX Tier 2 Fee is required")
                        .min(
                            tieredDefaults.amex_tier_two_fee?.minimum || tiered_validation.amex_tier_two_fee.minimum,
                            `Please enter a fee of $${(tieredDefaults.amex_tier_two_fee?.minimum || tiered_validation.amex_tier_two_fee.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            // AMEX Tier 3
            amex_tier_three_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "tiered",
                    then: Yup.number()
                        .required("AMEX Tier 3 Rate is required")
                        .min(
                            tieredDefaults.amex_tier_three_rate?.minimum || tiered_validation.amex_tier_three_rate.minimum,
                            `Please enter a rate of ${(tieredDefaults.amex_tier_three_rate?.minimum || tiered_validation.amex_tier_three_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),
            amex_tier_three_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "tiered",
                    then: Yup.number()
                        .required("AMEX Tier 3 Fee is required")
                        .min(
                            tieredDefaults.amex_tier_three_fee?.minimum || tiered_validation.amex_tier_three_fee.minimum,
                            `Please enter a fee of $${(tieredDefaults.amex_tier_three_fee?.minimum || tiered_validation.amex_tier_three_fee.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            // AMEX Tier 4
            amex_tier_four_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "tiered",
                    then: Yup.number()
                        .required("AMEX Tier 4 Rate is required")
                        .min(
                            tieredDefaults.amex_tier_four_rate?.minimum || tiered_validation.amex_tier_four_rate.minimum,
                            `Please enter a rate of ${(tieredDefaults.amex_tier_four_rate?.minimum || tiered_validation.amex_tier_four_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),
            amex_tier_four_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "tiered",
                    then: Yup.number()
                        .required("AMEX Tier 4 Fee is required")
                        .min(
                            tieredDefaults.amex_tier_four_fee?.minimum || tiered_validation.amex_tier_four_fee.minimum,
                            `Please enter a fee of $${(tieredDefaults.amex_tier_four_fee?.minimum || tiered_validation.amex_tier_four_fee.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            // AMEX Flat Rate
            tiered_amex_flat_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "flat_rate",
                    then: Yup.number()
                        .required("AMEX Flat Rate is required")
                        .min(
                            tieredDefaults.tiered_amex_flat_rate?.minimum || tiered_validation.tiered_amex_flat_rate.minimum,
                            `Please enter a rate of ${(tieredDefaults.tiered_amex_flat_rate?.minimum || tiered_validation.tiered_amex_flat_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number(),
                }),

            tiered_amex_flat_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "flat_rate",
                    then: Yup.number()
                        .required("AMEX Flat Transaction Fee is required")
                        .min(
                            tieredDefaults.tiered_amex_flat_transaction?.minimum || tiered_validation.tiered_amex_flat_transaction.minimum,
                            `Please enter a fee of $${(tieredDefaults.tiered_amex_flat_transaction?.minimum || tiered_validation.tiered_amex_flat_transaction.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number(),
                }),

            // AMEX Interchange Plus
            tiered_amex_ic_plus_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "ic_plus",
                    then: Yup.number()
                        .required("AMEX IC+ Rate is required")
                        .min(
                            tieredDefaults.tiered_amex_ic_plus_rate?.minimum || tiered_validation.tiered_amex_ic_plus_rate.minimum,
                            `Please enter a rate of ${(tieredDefaults.tiered_amex_ic_plus_rate?.minimum || tiered_validation.tiered_amex_ic_plus_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number(),
                }),

            tiered_amex_ic_plus_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("tiered_amex_structure", {
                    is: "ic_plus",
                    then: Yup.number()
                        .required("AMEX IC+ Transaction Fee is required")
                        .min(
                            tieredDefaults.tiered_amex_ic_plus_transaction?.minimum || tiered_validation.tiered_amex_ic_plus_transaction.minimum,
                            `Please enter a fee of $${(tieredDefaults.tiered_amex_ic_plus_transaction?.minimum || tiered_validation.tiered_amex_ic_plus_transaction.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number(),
                }),

            tiered_pin_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Rate is required")
                .min(
                    tieredDefaults.tiered_pin_debit_rate?.minimum || tiered_validation.tiered_pin_debit_rate.minimum,
                    `Please enter a rate of ${(tieredDefaults.tiered_pin_debit_rate?.minimum || tiered_validation.tiered_pin_debit_rate.minimum).toFixed(2)}% or higher.`,
                ),

            tiered_pin_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Per Transaction fee is required")
                .min(
                    tieredDefaults.tiered_pin_debit_per_transaction?.minimum || tiered_validation.tiered_pin_debit_per_transaction.minimum,
                    `Please enter a fee of $${(tieredDefaults.tiered_pin_debit_per_transaction?.minimum || tiered_validation.tiered_pin_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            tiered_pin_debit_network_fee: Yup.string().required(
                "Network Fee selection is required",
            ),

            fnf_number: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("ebt", {
                    is: "yes",
                    then: Yup.number().required("FNF Number is Required"),
                    otherwise: Yup.number(),
                }),
        });
    }, [tieredDefaults]);

    const handleOnChange = (e) => {
        try {
            e?.persist();
        } catch (error) {}
        const updatedFormData = {
            ...form_data,
            [e.target.name]: e.target.value,
        };

        setFormDataByKeyValue(e.target.name, e.target.value);
        validationSchema
            .validateAt(e.target.name, updatedFormData)
            .then((e) => {
                // nothing here
            })
            .catch((err) => {
                let validation_errors = errors;
                validation_errors = {
                    ...validation_errors,
                    [e.target.name]: err.errors,
                };
                setErrors(validation_errors);
            });
    };

    return (
        <div className="dual-pricing-section tiered-pricing">
            <div className="container-col-2">
                <div className="top-label-box">
                    <div className="top-label">
                        <span>Qualified</span>
                        <img src="/images/visa.png" />
                        <img src="/images/mastercard.png" />
                        <img src="/images/discover.png" />
                    </div>
                    <div className="top-label-box-content">
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            className="container-col-2"
                        >
                            <div className="w-100">
                                <PercentageInput
                                    label="Qual Rate"
                                    placeholder={tieredDefaults.tiered_qual_rate?.default || "0.00"}
                                    name={"tiered_qual_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.tiered_qual_rate}
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_qual_rate?.soft_limit}
                                />
                                {errors?.tiered_qual_rate &&
                                    Array.isArray(errors.tiered_qual_rate) &&
                                    errors.tiered_qual_rate.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Txn Fee"
                                    placeholder={tieredDefaults.tiered_qual_transaction?.default || "0.10"}
                                    name={"tiered_qual_transaction"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.tiered_qual_transaction}
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_qual_transaction?.soft_limit}
                                />
                                {errors?.tiered_qual_transaction &&
                                    Array.isArray(
                                        errors.tiered_qual_transaction,
                                    ) &&
                                    errors.tiered_qual_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                            <div className="w-100">
                                <PercentageInput
                                    label="Debit Rate"
                                    placeholder={tieredDefaults.tiered_qual_debit_rate?.default || "0.00"}
                                    name={"tiered_qual_debit_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.tiered_qual_debit_rate}
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_qual_debit_rate?.soft_limit}
                                />
                                {errors?.tiered_qual_debit_rate &&
                                    Array.isArray(
                                        errors.tiered_qual_debit_rate,
                                    ) &&
                                    errors.tiered_qual_debit_rate.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Debit Per Txn"
                                    placeholder={tieredDefaults.tiered_qual_debit_per_transaction?.default || "0.10"}
                                    name={"tiered_qual_debit_per_transaction"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={
                                        form_data.tiered_qual_debit_per_transaction
                                    }
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_qual_debit_per_transaction?.soft_limit}
                                />
                                {errors?.tiered_qual_debit_per_transaction &&
                                    Array.isArray(
                                        errors.tiered_qual_debit_per_transaction,
                                    ) &&
                                    errors.tiered_qual_debit_per_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                        </Stack>
                    </div>
                </div>
            </div>

            <div className="container-col-2">
                <div className="top-label-box">
                    <div className="top-label">
                        <span>Mid-Qualified</span>
                        <img src="/images/visa.png" />
                        <img src="/images/mastercard.png" />
                        <img src="/images/discover.png" />
                    </div>
                    <div className="top-label-box-content">
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            className="container-col-2"
                        >
                            <div className="w-100">
                                <PercentageInput
                                    label="Mid-Qual Rate"
                                    placeholder={tieredDefaults.tiered_mid_qual_rate?.default || "0.00"}
                                    name={"tiered_mid_qual_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.tiered_mid_qual_rate}
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_mid_qual_rate?.soft_limit}
                                />
                                {errors?.tiered_mid_qual_rate &&
                                    Array.isArray(
                                        errors.tiered_mid_qual_rate,
                                    ) &&
                                    errors.tiered_mid_qual_rate.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Txn Fee"
                                    placeholder={tieredDefaults.tiered_mid_qual_transaction?.default || "0.10"}
                                    name={"tiered_mid_qual_transaction"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={
                                        form_data.tiered_mid_qual_transaction
                                    }
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_mid_qual_transaction?.soft_limit}
                                />
                                {errors?.tiered_mid_qual_transaction &&
                                    Array.isArray(
                                        errors.tiered_mid_qual_transaction,
                                    ) &&
                                    errors.tiered_mid_qual_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                            <div className="w-100">
                                <PercentageInput
                                    label="Debit Rate"
                                    placeholder={tieredDefaults.tiered_mid_qual_debit_rate?.default || "0.00"}
                                    name={"tiered_mid_qual_debit_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.tiered_mid_qual_debit_rate}
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_mid_qual_debit_rate?.soft_limit}
                                />
                                {errors?.tiered_mid_qual_debit_rate &&
                                    Array.isArray(
                                        errors.tiered_mid_qual_debit_rate,
                                    ) &&
                                    errors.tiered_mid_qual_debit_rate.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Debit Per Txn"
                                    placeholder={tieredDefaults.tiered_mid_qual_debit_per_transaction?.default || "0.10"}
                                    name={
                                        "tiered_mid_qual_debit_per_transaction"
                                    }
                                    type="number"
                                    onChange={handleOnChange}
                                    value={
                                        form_data.tiered_mid_qual_debit_per_transaction
                                    }
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_mid_qual_debit_per_transaction?.soft_limit}
                                />
                                {errors?.tiered_mid_qual_debit_per_transaction &&
                                    Array.isArray(
                                        errors.tiered_mid_qual_debit_per_transaction,
                                    ) &&
                                    errors.tiered_mid_qual_debit_per_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                        </Stack>
                    </div>
                </div>
            </div>

            <div className="container-col-2">
                <div className="top-label-box">
                    <div className="top-label">
                        <span>Non-Qualified</span>
                        <img src="/images/visa.png" />
                        <img src="/images/mastercard.png" />
                        <img src="/images/discover.png" />
                    </div>
                    <div className="top-label-box-content">
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            className="container-col-2"
                        >
                            <div className="w-100">
                                <PercentageInput
                                    label="Non-Qual Rate"
                                    placeholder={tieredDefaults.tiered_non_qual_rate?.default || "0.00"}
                                    name={"tiered_non_qual_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.tiered_non_qual_rate}
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_non_qual_rate?.soft_limit}
                                />
                                {errors?.tiered_non_qual_rate &&
                                    Array.isArray(
                                        errors.tiered_non_qual_rate,
                                    ) &&
                                    errors.tiered_non_qual_rate.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Txn Fee"
                                    placeholder={tieredDefaults.tiered_non_qual_transaction?.default || "0.10"}
                                    name={"tiered_non_qual_transaction"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={
                                        form_data.tiered_non_qual_transaction
                                    }
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_non_qual_transaction?.soft_limit}
                                />
                                {errors?.tiered_non_qual_transaction &&
                                    Array.isArray(
                                        errors.tiered_non_qual_transaction,
                                    ) &&
                                    errors.tiered_non_qual_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                            <div className="w-100">
                                <PercentageInput
                                    label="Debit Rate"
                                    placeholder={tieredDefaults.tiered_non_qual_debit_rate?.default || "0.00"}
                                    name={"tiered_non_qual_debit_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.tiered_non_qual_debit_rate}
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_non_qual_debit_rate?.soft_limit}
                                />
                                {errors?.tiered_non_qual_debit_rate &&
                                    Array.isArray(
                                        errors.tiered_non_qual_debit_rate,
                                    ) &&
                                    errors.tiered_non_qual_debit_rate.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Debit Per Txn"
                                    placeholder={tieredDefaults.tiered_non_qual_debit_per_transaction?.default || "0.10"}
                                    name={
                                        "tiered_non_qual_debit_per_transaction"
                                    }
                                    type="number"
                                    onChange={handleOnChange}
                                    value={
                                        form_data.tiered_non_qual_debit_per_transaction
                                    }
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_non_qual_debit_per_transaction?.soft_limit}
                                />
                                {errors?.tiered_non_qual_debit_per_transaction &&
                                    Array.isArray(
                                        errors.tiered_non_qual_debit_per_transaction,
                                    ) &&
                                    errors.tiered_non_qual_debit_per_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                        </Stack>
                    </div>
                </div>
            </div>

            <div className="equipment-box mt-2r">
                <p className="box-label">PIN-based Debit</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-60">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue(
                                    "tiered_pin_debit_network_fee",
                                    value,
                                )
                            }
                            value={form_data.tiered_pin_debit_network_fee}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="tiered_pin_debit_network_fee"
                            label="Network Fee"
                        />
                        {errors?.tiered_pin_debit_network_fee &&
                            Array.isArray(
                                errors.tiered_pin_debit_network_fee,
                            ) &&
                            errors.tiered_pin_debit_network_fee.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    <div className="w-100">
                        <PercentageInput
                            label="Debit Rate"
                            type="number"
                            placeholder={tieredDefaults.tiered_pin_debit_rate?.default || "0.5"}
                            name={"tiered_pin_debit_rate"}
                            onChange={handleOnChange}
                            value={form_data.tiered_pin_debit_rate}
                            errors={errors}
                            softLimit={tieredDefaults?.tiered_pin_debit_rate?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.tiered_pin_debit_network_fee}
                        />
                        {errors?.tiered_pin_debit_rate &&
                            Array.isArray(errors.tiered_pin_debit_rate) &&
                            errors.tiered_pin_debit_rate.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    <div className="w-100">
                        <CurrencyInput
                            label="Per Txn"
                            type="number"
                            placeholder={tieredDefaults.tiered_pin_debit_per_transaction?.default || "0.15"}
                            name={"tiered_pin_debit_per_transaction"}
                            onChange={handleOnChange}
                            value={form_data.tiered_pin_debit_per_transaction}
                            errors={errors}
                            softLimit={tieredDefaults?.tiered_pin_debit_per_transaction?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.tiered_pin_debit_network_fee}
                        />
                        {errors?.tiered_pin_debit_per_transaction &&
                            Array.isArray(
                                errors.tiered_pin_debit_per_transaction,
                            ) &&
                            errors.tiered_pin_debit_per_transaction.map(
                                (error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                </Stack>
            </div>

            <div className="equipment-box mt-2r">
                <p className="box-label">ACH</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-100 d-flex">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("add_ach", value)
                            }
                            value={form_data.add_ach}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="add_ach"
                            label="Add ACH"
                        />
                    </div>
                </Stack>
            </div>

            {form_data.add_ach === "yes" && (
                <ACHPricing
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeErrorEq}
                    isAddOn={true}
                />
            )}

            <div className="equipment-box mt-2r ebt-transaction">
                <p className="box-label">EBT Transactions</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="ebt-switch">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("ebt", value)
                            }
                            value={form_data.ebt}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="ebt"
                            label="EBT"
                        />
                        {errors?.ebt &&
                            Array.isArray(errors.ebt) &&
                            errors.ebt.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    {form_data.ebt == "yes" && (
                        <div className="d-flex w-100">
                            <div className="floating-form w-100">
                                <Form.Group size="lg" controlId="fnf_number">
                                    <Form.Control
                                        placeholder="FNS Number"
                                        type="text"
                                        name="fnf_number"
                                        value={form_data.fnf_number}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                handleOnChange(e);
                                            }
                                        }}
                                        className="input-control"
                                    />
                                    <Form.Label className="floating-label">
                                        FNF Number
                                    </Form.Label>
                                </Form.Group>
                                {errors?.fnf_number &&
                                    Array.isArray(errors.fnf_number) &&
                                    errors.fnf_number.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="floating-form w-100">
                                <CurrencyInput
                                    label="EBT Transactions"
                                    endAdornment="/txn"
                                    placeholder={
                                        EMS_OTHER_DEFAULT_FEE.ebt_transactions
                                    }
                                    value={form_data.ebt_transactions}
                                    name={"ebt_transactions"}
                                    type="number"
                                    onChange={handleOnChange}
                                    errors={errors}
                                />
                                {errors?.ebt_transactions &&
                                    Array.isArray(errors.ebt_transactions) &&
                                    errors.ebt_transactions.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}
                </Stack>
            </div>

            <p className="sub-heading-lead mt-4r">
                Select your preferred AMEX structure
            </p>
            <div className="pricing-model-buttons ">
                <button
                    type="button"
                    className={
                        form_data.tiered_amex_structure == "flat_rate"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setFormDataByKeyValue(
                            "tiered_amex_structure",
                            "flat_rate",
                        )
                    }
                >
                    {form_data.tiered_amex_structure == "flat_rate" ? (
                        <IoMdCheckmark />
                    ) : (
                        ""
                    )}{" "}
                    Flat Rate
                </button>
                <button
                    type="button"
                    className={
                        form_data.tiered_amex_structure == "tiered"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setFormDataByKeyValue("tiered_amex_structure", "tiered")
                    }
                >
                    {form_data.tiered_amex_structure == "tiered" ? (
                        <IoMdCheckmark />
                    ) : (
                        ""
                    )}{" "}
                    Tiered Rate
                </button>
                <button
                    type="button"
                    className={
                        form_data.tiered_amex_structure == "ic_plus"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setFormDataByKeyValue(
                            "tiered_amex_structure",
                            "ic_plus",
                        )
                    }
                >
                    {form_data.tiered_amex_structure == "ic_plus" ? (
                        <IoMdCheckmark />
                    ) : (
                        ""
                    )}{" "}
                    IC+
                </button>
            </div>

            {form_data.tiered_amex_structure == "tiered" && (
                <>
                    <div className="top-label-box">
                        <div className="top-label">
                            <span>AMEX Tier 1</span>
                            <img src="/images/amex.png" />
                        </div>
                        <div className="top-label-box-content">
                            <div className="container-col-2">
                                <div className="w-100">
                                    <PercentageInput
                                        label="Rate"
                                        placeholder={tieredDefaults.amex_tier_one_rate?.default || "0.00"}
                                        name={"amex_tier_one_rate"}
                                        type="number"
                                        onChange={handleOnChange}
                                        value={form_data.amex_tier_one_rate}
                                        errors={errors}
                                        softLimit={tieredDefaults?.amex_tier_one_rate?.soft_limit}
                                    />
                                    {errors?.amex_tier_one_rate &&
                                        Array.isArray(
                                            errors.amex_tier_one_rate,
                                        ) &&
                                        errors.amex_tier_one_rate.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                                <div className="w-100">
                                    <CurrencyInput
                                        label="Txn Fee"
                                        placeholder={tieredDefaults.amex_tier_one_fee?.default || "0.10"}
                                        name={"amex_tier_one_fee"}
                                        type="number"
                                        onChange={handleOnChange}
                                        value={form_data.amex_tier_one_fee}
                                        errors={errors}
                                        softLimit={tieredDefaults?.amex_tier_one_fee?.soft_limit}
                                    />
                                    {errors?.amex_tier_one_fee &&
                                        Array.isArray(
                                            errors.amex_tier_one_fee,
                                        ) &&
                                        errors.amex_tier_one_fee.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="top-label-box">
                        <div className="top-label">
                            <span>AMEX Tier 2</span>
                            <img src="/images/amex.png" />
                        </div>
                        <div className="top-label-box-content">
                            <div className="container-col-2">
                                <div className="w-100">
                                    <PercentageInput
                                        label="Rate"
                                        placeholder={tieredDefaults.amex_tier_two_rate?.default || "0.00"}
                                        name={"amex_tier_two_rate"}
                                        type="number"
                                        onChange={handleOnChange}
                                        value={form_data.amex_tier_two_rate}
                                        errors={errors}
                                        softLimit={tieredDefaults?.amex_tier_two_rate?.soft_limit}
                                    />
                                    {errors?.amex_tier_two_rate &&
                                        Array.isArray(
                                            errors.amex_tier_two_rate,
                                        ) &&
                                        errors.amex_tier_two_rate.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                                <div className="w-100">
                                    <CurrencyInput
                                        label="Txn Fee"
                                        placeholder={tieredDefaults.amex_tier_two_fee?.default || "0.10"}
                                        name={"amex_tier_two_fee"}
                                        type="number"
                                        onChange={handleOnChange}
                                        value={form_data.amex_tier_two_fee}
                                        errors={errors}
                                        softLimit={tieredDefaults?.amex_tier_two_fee?.soft_limit}
                                    />
                                    {errors?.amex_tier_two_fee &&
                                        Array.isArray(
                                            errors.amex_tier_two_fee,
                                        ) &&
                                        errors.amex_tier_two_fee.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="top-label-box">
                        <div className="top-label">
                            <span>AMEX Tier 3</span>
                            <img src="/images/amex.png" />
                        </div>
                        <div className="top-label-box-content">
                            <div className="container-col-2">
                                <div className="w-100">
                                    <PercentageInput
                                        label="Rate"
                                        placeholder={tieredDefaults.amex_tier_three_rate?.default || "0.00"}
                                        name={"amex_tier_three_rate"}
                                        type="number"
                                        onChange={handleOnChange}
                                        value={form_data.amex_tier_three_rate}
                                        errors={errors}
                                        softLimit={tieredDefaults?.amex_tier_three_rate?.soft_limit}
                                    />
                                    {errors?.amex_tier_three_rate &&
                                        Array.isArray(
                                            errors.amex_tier_three_rate,
                                        ) &&
                                        errors.amex_tier_three_rate.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                                <div className="w-100">
                                    <CurrencyInput
                                        label="Txn Fee"
                                        placeholder={tieredDefaults.amex_tier_three_fee?.default || "0.10"}
                                        name={"amex_tier_three_fee"}
                                        type="number"
                                        onChange={handleOnChange}
                                        value={form_data.amex_tier_three_fee}
                                        errors={errors}
                                        softLimit={tieredDefaults?.amex_tier_three_fee?.soft_limit}
                                    />
                                    {errors?.amex_tier_three_fee &&
                                        Array.isArray(
                                            errors.amex_tier_three_fee,
                                        ) &&
                                        errors.amex_tier_three_fee.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="top-label-box">
                        <div className="top-label">
                            <span>AMEX Tier 4</span>
                            <img src="/images/amex.png" />
                        </div>
                        <div className="top-label-box-content">
                            <div className="container-col-2">
                                <div className="w-100">
                                    <PercentageInput
                                        label="Rate"
                                        placeholder={tieredDefaults.amex_tier_four_rate?.default || "0.00"}
                                        name={"amex_tier_four_rate"}
                                        type="number"
                                        onChange={handleOnChange}
                                        value={form_data.amex_tier_four_rate}
                                        errors={errors}
                                        softLimit={tieredDefaults?.amex_tier_four_rate?.soft_limit}
                                    />
                                    {errors?.amex_tier_four_rate &&
                                        Array.isArray(
                                            errors.amex_tier_four_rate,
                                        ) &&
                                        errors.amex_tier_four_rate.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                                <div className="w-100">
                                    <CurrencyInput
                                        label="Txn Fee"
                                        placeholder={tieredDefaults.amex_tier_four_fee?.default || "0.10"}
                                        name={"amex_tier_four_fee"}
                                        type="number"
                                        onChange={handleOnChange}
                                        value={form_data.amex_tier_four_fee}
                                        errors={errors}
                                        softLimit={tieredDefaults?.amex_tier_four_fee?.soft_limit}
                                    />
                                    {errors?.amex_tier_four_fee &&
                                        Array.isArray(
                                            errors.amex_tier_four_fee,
                                        ) &&
                                        errors.amex_tier_four_fee.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {form_data.tiered_amex_structure == "flat_rate" && (
                <div className="top-label-box">
                    <div className="top-label">
                        <span>AMEX Flat Rate</span>
                        <img src="/images/amex.png" />
                    </div>
                    <div className="top-label-box-content">
                        <div className="container-col-2">
                            <div className="w-100">
                                <CurrencyInput
                                    label="Flat Rate"
                                    placeholder={tieredDefaults.tiered_amex_flat_rate?.default || "0.10"}
                                    name={"tiered_amex_flat_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.tiered_amex_flat_rate}
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_amex_flat_rate?.soft_limit}
                                />
                                {errors?.tiered_amex_flat_rate &&
                                    Array.isArray(
                                        errors.tiered_amex_flat_rate,
                                    ) &&
                                    errors.tiered_amex_flat_rate.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Txn Fee"
                                    placeholder={tieredDefaults.tiered_amex_flat_transaction?.default || "0.10"}
                                    name={"tiered_amex_flat_transaction"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={
                                        form_data.tiered_amex_flat_transaction
                                    }
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_amex_flat_transaction?.soft_limit}
                                />
                                {errors?.tiered_amex_flat_transaction &&
                                    Array.isArray(
                                        errors.tiered_amex_flat_transaction,
                                    ) &&
                                    errors.tiered_amex_flat_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {form_data.tiered_amex_structure == "ic_plus" && (
                <div className="top-label-box">
                    <div className="top-label">
                        <span>AMEX Interchange Plus</span>
                        <img src="/images/amex.png" />
                    </div>
                    <div className="top-label-box-content">
                        <div className="container-col-2">
                            <div className="w-100">
                                <PercentageInput
                                    label="IC+ Rate"
                                    placeholder={tieredDefaults.tiered_amex_ic_plus_rate?.default || "0.10"}
                                    name={"tiered_amex_ic_plus_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.tiered_amex_ic_plus_rate}
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_amex_ic_plus_rate?.soft_limit}
                                />
                                {errors?.tiered_amex_ic_plus_rate &&
                                    Array.isArray(
                                        errors.tiered_amex_ic_plus_rate,
                                    ) &&
                                    errors.tiered_amex_ic_plus_rate.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Txn Fee"
                                    placeholder={tieredDefaults.tiered_amex_ic_plus_transaction?.default || "0.10"}
                                    name={"tiered_amex_ic_plus_transaction"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={
                                        form_data.tiered_amex_ic_plus_transaction
                                    }
                                    errors={errors}
                                    softLimit={tieredDefaults?.tiered_amex_ic_plus_transaction?.soft_limit}
                                />
                                {errors?.tiered_amex_ic_plus_transaction &&
                                    Array.isArray(
                                        errors.tiered_amex_ic_plus_transaction,
                                    ) &&
                                    errors.tiered_amex_ic_plus_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <DefaultFee
                form_data={form_data}
                setFormDataByKeyValue={setFormDataByKeyValue}
                errors={errors}
            />

            {isEditable && (
                <Equipments
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    form_data={form_data}
                    errors={errors}
                    allEquipments={allEquipments}
                    removeError={removeErrorEq}
                />
            )}
        </div>
    );
};

export default TieredPricing;
